// IMP-389: the order-summary block for the agent-purchase confirmation —
// product name, recurring monthly price, included-usage lines, and (only when
// the charge is prorated) the exact "Due today" amount. The caller supplies the
// outer card/well styling so it can bleed to a Dialog's edges.
//
// IMP-394: re-adds the free-trial states (ported from the design/purchase-modal
// prototype). A trial zeroes today's charge, so it's the headline ("N days
// free") and there's never a "Due today" block. The first charge lands at
// trial-end: for a first purchase it's the plain monthly (folded into the
// descriptor); for a mid-cycle add it's a prorated bridge shown in its own
// "Due {date}" block.

export function formatUsd(cents: number): string {
  const amount = cents / 100
  const hasCents = Math.round(amount * 100) % 100 !== 0
  return amount.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: hasCents ? 2 : 0,
    maximumFractionDigits: 2,
  })
}

// Format a "YYYY-MM-DD" server date as "Jul 13" in local time without a
// timezone shift (constructing from parts keeps it a local calendar date).
export function formatShortDate(iso: string): string {
  const [y, m, d] = iso.split("-").map(Number)
  return new Date(y, m - 1, d).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  })
}

// IMP-394: the trial shape sent on the pricing prop when a free trial applies.
export interface TrialInfo {
  days: number
  trial_end_date: string
  kind: "first_purchase" | "mid_cycle"
  // Mid-cycle add — the first charge at trial-end is a prorated bridge.
  prorated: boolean
  first_charge_cents: number
  next_anchor_date: string
}

export interface AgentPurchaseSummaryProps {
  name: string
  monthlyCents: number
  // One string per included-usage line (minutes, texts). Empty → no line shown.
  includes: string[]
  // Mid-cycle add — today's charge is a proration, shown as "Due today". When
  // false the charge equals the full monthly, so no separate block is needed.
  prorated: boolean
  dueTodayCents: number
  // When present, the purchase is a free trial: $0 today, billing at trial-end.
  trial?: TrialInfo | null
}

export function AgentPurchaseSummary({
  name,
  monthlyCents,
  includes,
  prorated,
  dueTodayCents,
  trial,
}: AgentPurchaseSummaryProps) {
  const monthlyLabel = formatUsd(monthlyCents)

  // Hero: the trial is what's happening today, so it leads.
  const heroAmount = trial ? `${trial.days} days free` : monthlyLabel
  const heroDescriptor = trial
    ? // Mid-cycle trials carry their first-charge detail in the block below, so
      // no descriptor there; a first-purchase trial states the plain monthly.
      trial.prorated
      ? null
      : `then ${monthlyLabel}/month, starting ${formatShortDate(trial.trial_end_date)}`
    : prorated
      ? "per month"
      : "per month, starting today"

  const showDueToday = prorated && !trial
  const showFirstChargeAfterTrial = Boolean(trial?.prorated)

  return (
    <>
      <div>
        <p className="text-sm font-medium">{name}</p>
        <p className="mt-2 flex items-baseline gap-2">
          <span className="text-4xl font-medium">{heroAmount}</span>
          {heroDescriptor && (
            <span className="text-muted-foreground text-sm">
              {heroDescriptor}
            </span>
          )}
        </p>

        {includes.length > 0 &&
          (includes.length === 1 ? (
            <p className="text-muted-foreground mt-4 text-sm">
              Includes {includes[0]}
            </p>
          ) : (
            <div className="mt-4">
              <p className="text-muted-foreground text-sm">Includes:</p>
              <ul className="text-muted-foreground mt-2 list-disc space-y-1 pl-5 text-sm">
                {includes.map((line) => (
                  <li key={line}>{line}</li>
                ))}
              </ul>
            </div>
          ))}
      </div>

      {showDueToday && (
        <div className="border-t pt-4">
          <p className="text-sm font-medium">Due today</p>
          <p className="mt-2 text-xl font-medium">{formatUsd(dueTodayCents)}</p>
          <p className="text-muted-foreground mt-2 text-sm">
            Prorated through your next billing date.
          </p>
        </div>
      )}

      {showFirstChargeAfterTrial && trial && (
        <div className="border-t pt-4">
          <p className="text-sm font-medium">
            Due {formatShortDate(trial.trial_end_date)}
          </p>
          <p className="mt-2 text-xl font-medium">
            {formatUsd(trial.first_charge_cents)}
          </p>
          <p className="text-muted-foreground mt-2 text-sm">
            Prorated through your next billing date. Then {monthlyLabel}/month
            starting {formatShortDate(trial.next_anchor_date)}.
          </p>
        </div>
      )}
    </>
  )
}
